import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import config from '../config/path';
import { authProvider } from '../authProvider';
import * as Sentry from '@sentry/react';
import { tryParse } from './tryParse';
import { HttpError } from '@refinedev/core';
import i18n from 'i18next';

const api = axios.create({
    baseURL: config.API,
    timeout: 50000,
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
    validateStatus: status => (status >= 200 && status < 300) || status === 415,
});

api.interceptors.request.use(
    // @ts-ignore
    function (config: AxiosRequestConfig) {
        const token = localStorage.getItem('auth');

        if (token) {
            config.headers!.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    function (error: object) {
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    function (response: AxiosResponse) {
        return response;
    },
    async function (error: any) {
        const statusCode = error.response?.status as number | undefined;
        const url = error?.config?.url;
        const method = error?.config?.method?.toUpperCase();
        const data = error?.config?.data;
        const responseData = error?.response?.data;

        if (statusCode && statusCode >= 400) {
            const message =
                responseData?.message ||
                `HTTP ${statusCode} error on ${method} ${url}`;

            Sentry.withScope(scope => {
                scope.setTag('http.status', String(statusCode));
                scope.setTag('http.method', method || 'UNKNOWN');
                scope.setTag('http.url', url || 'unknown');

                scope.setExtras({
                    requestData: tryParse(data),
                    responseErrors: responseData?.errors,
                    fullResponse: responseData,
                });

                Sentry.captureException(new Error(message));
            });
        }

        if (statusCode === 401) {
            return authProvider.logout('');
        }

        const dictionaryErrors: Record<number, string> = {
            403: i18n.t('notifications.accessDenied'),
            404: i18n.t('notifications.data_fetch_error'),
            422: i18n.t('notifications.entry_error'),
            429: i18n.t('notifications.too_many_attempts'),
            500: i18n.t('notifications.an_error_occurred'),
        };

        const rawMessage = error.response?.data?.message;
        const translationKey =
            typeof rawMessage === 'string' && rawMessage.length
                ? `notifications.${rawMessage}`
                : '';
        const translatedMessage = translationKey
            ? i18n.exists(translationKey)
                ? i18n.t(translationKey)
                : undefined
            : undefined;

        let message =
            translatedMessage ||
            (statusCode ? dictionaryErrors[statusCode] : undefined) ||
            (typeof rawMessage === 'string' ? rawMessage : undefined);

        if (!message) {
            message = statusCode
                ? i18n.t('notifications.an_error_occurred')
                : i18n.t('notifications.data_fetch_error');
        }

        const customError: HttpError = {
            ...error,
            message,
            statusCode,
        };

        return Promise.reject(customError);
    }
);

export { api };
